/**
 * Which catalogue pages should stay out of the index.
 *
 * The 2026-10-01 production audit found the sitemap advertising adult tags
 * (#porn, #pussy, #nsfw …), ~115 adult pack slugs, 66 hashtags that are Gmail
 * addresses, phone-number hashtags and ~80 packs from one phone-number spammer
 * ("insta-ayan6v-03454982068-…") — all `index, follow`.
 * The owner's call: keep those pages reachable but `noindex, follow` them and
 * drop them from the sitemap. Ads stay — if Google objects to a page, the owner
 * removes that pack by hand. Listing grids are NOT filtered either: what a
 * visitor sees in a grid is the API's business.
 *
 * Pure and dependency-free: image captions run in the browser too (LoadMoreClient,
 * LocalizedFeed), so the caption stoplist must give the same answer on both
 * sides. The rollout switch lives in content-policy-flag.ts, server-side only.
 *
 * Tuned against real offenders AND real near-misses. Anything that matches as a
 * bare substring must be unambiguous inside a concatenated hashtag
 * ("loveandsex", "trymebitch"); anything that is a substring of ordinary words
 * ("sex" in Essex/unisex, "boob" in booboo) only matches as a whole token.
 */

export type FlagReason = "adult" | "pii" | "spam" | "junk";

/** Unambiguous wherever they appear, including inside run-together hashtags. */
const ADULT_SUBSTRINGS = [
  "fuck",
  "pussy",
  "porn",
  "xxx",
  "nsfw",
  "hentai",
  "horny",
  "bitch",
  "motherf",
  "chutiy",
  "madarchod",
  "behenchod",
  "bhosd",
];

/** Only as a whole word: each is a substring of something innocent. */
const ADULT_TOKENS = new Set([
  "sex",
  "sexy",
  "sexi",
  "sexo",
  "sexting",
  "nude",
  "nudes",
  "naked",
  "boob",
  "boobs",
  "boobies",
  "tits",
  "titties",
  "dick",
  "penis",
  "vagina",
  "slut",
  "whore",
  "adult",
  "18plus",
  "gandu",
  "lund",
  "randi",
]);

/** Run-together tags the token rule cannot see. Add sparingly, with evidence. */
const ADULT_TAGS = new Set(["loveandsex", "adultemojis"]);

/** Proper names that contain an adult substring. */
const ALLOWED = ["xxxtentacion"];

/**
 * Short tags that are real themes people search for ("ai stickers", "f1",
 * "gf"), checked against the 2026-10 sitemap's junk list. Everything else of
 * two characters or fewer ("xd", "a", "hi", ":v") is noise.
 */
const SHORT_ALLOWED = new Set([
  "ai",
  "3d",
  "4k",
  "hd",
  "f1",
  "1d",
  "tv",
  "uk",
  "us",
  "dj",
  "mj",
  "gf",
  "bf",
]);

/** One uploader flooded the catalogue with these; they are spam, not stickers. */
const SPAM = /ayaa?n6v|03454982068/;

const EMAIL =
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}|(?:gmail|yahoo|hotmail|outlook|icloud)(?:\.?com)?/;
/** Eight or more digits in a row is a phone number, not a sticker theme. */
const PHONE = /\d{8,}/;

function normalise(value: string | null | undefined): string {
  return String(value ?? "")
    .normalize("NFKC")
    .trim()
    .toLowerCase();
}

function tokens(text: string): string[] {
  return text.split(/[^\p{L}\p{N}+]+/u).filter(Boolean);
}

function isAdult(text: string): boolean {
  if (!text) return false;
  const scrubbed = ALLOWED.reduce((acc, name) => acc.replaceAll(name, " "), text);
  if (ADULT_SUBSTRINGS.some((s) => scrubbed.includes(s))) return true;
  return tokens(scrubbed).some((t) => ADULT_TOKENS.has(t) || ADULT_TAGS.has(t));
}

function isPii(text: string): boolean {
  return EMAIL.test(text) || PHONE.test(text);
}

/**
 * Why a hashtag should stay out of the index, or null when it is fine.
 *
 * Junk is ASCII-only on purpose: "حب" (love) and "ਪਿਆਰ" are short in UTF-16
 * but are real words, while "xd", ":v" and "1" are not themes anyone searches.
 */
export function tagFlag(tag: string | null | undefined): FlagReason | null {
  const t = normalise(tag);
  if (!t) return "junk";
  if (SPAM.test(t)) return "spam";
  if (isPii(t)) return "pii";
  if (isAdult(t) || ADULT_TAGS.has(t)) return "adult";

  const ascii = [...t].every((ch) => ch.charCodeAt(0) < 0x80);
  if (
    ascii &&
    !SHORT_ALLOWED.has(t) &&
    (t.replace(/[^a-z0-9]/g, "").length <= 2 || /^\d+$/.test(t))
  ) {
    return "junk";
  }
  return null;
}

export function isFlaggedTag(tag: string | null | undefined): boolean {
  return tagFlag(tag) !== null;
}

export interface FlaggablePack {
  post_title?: string | null;
  post_slug?: string | null;
  post_tags?: string | null;
  user_name?: string | null;
}

/**
 * Why a pack page should stay out of the index, or null when it is fine.
 *
 * The slug is checked for adult words and spam only — its trailing identifier
 * ("kzdoxu1hvh") is random and can contain digit runs, so it is never read as a
 * phone number. Tags count only for adult/spam/PII, never for "junk": a pack
 * tagged "xd" is still a real pack.
 */
export function packFlag(pack: FlaggablePack): FlagReason | null {
  const title = normalise(pack.post_title);
  const slug = normalise(pack.post_slug).replace(/-[a-z0-9]{10}$/, "");
  const uploader = normalise(pack.user_name);
  const tags = normalise(pack.post_tags)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  if ([title, slug, uploader, ...tags].some((v) => SPAM.test(v))) return "spam";
  if ([title, ...tags].some(isPii)) return "pii";
  if ([title, slug, ...tags].some((v) => isAdult(v) || ADULT_TAGS.has(v))) return "adult";
  return null;
}

export function isFlaggedPack(pack: FlaggablePack): boolean {
  return packFlag(pack) !== null;
}
